import React, { useEffect, useState } from 'react';
import { Pencil, Trash2, Plus, ClipboardCheck } from 'lucide-react';
import api from '../../services/api';
import { PageHeader, TableSkeleton, EmptyState, Pagination } from '../../components/PageParts';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const empty = { vehicle_id: '', name: '', maintenance_type_name: '', interval_km: '', interval_months: '', last_done_km: '', last_done_date: '', responsible_name: '', notes: '' };
const statusTone = (s) => ({ overdue: 'bg-red-100 text-red-700', due_soon: 'bg-amber-100 text-amber-800', ok: 'bg-emerald-100 text-emerald-700' }[s] || 'bg-neutral-200 text-neutral-700');
const statusLabel = (s) => ({ overdue: 'Vencida', due_soon: 'Próxima', ok: 'Em dia' }[s] || s);

export default function MaintenancePlans() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState([]);
  const [types, setTypes] = useState([]);
  const [statusF, setStatusF] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 10 };
      if (statusF !== 'all') params.plan_status = statusF;
      const { data } = await api.get('/maintenance-plans', { params });
      setItems(data.items); setTotal(data.total);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, statusF]);
  useEffect(() => {
    (async () => {
      const [v, mt] = await Promise.all([api.get('/vehicles', { params: { limit: 200 } }), api.get('/masters/maintenance-types', { params: { limit: 100 } }).catch(() => ({ data: { items: [] } }))]);
      setVehicles(v.data.items || []); setTypes(mt.data.items || []);
    })();
  }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setModalOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...empty, ...p, last_done_date: p.last_done_date ? p.last_done_date.slice(0, 10) : '' });
    setModalOpen(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id || !form.name) { toast.error('Veículo e nome obrigatórios'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        interval_km: form.interval_km ? Number(form.interval_km) : null,
        interval_months: form.interval_months ? Number(form.interval_months) : null,
        last_done_km: form.last_done_km ? Number(form.last_done_km) : null,
        last_done_date: form.last_done_date || null,
      };
      if (editing) await api.put(`/maintenance-plans/${editing.id}`, payload);
      else await api.post('/maintenance-plans', payload);
      toast.success(editing ? 'Plano atualizado' : 'Plano criado');
      setModalOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); } finally { setSaving(false); }
  };
  const confirmDelete = async () => { try { await api.delete(`/maintenance-plans/${deleteId}`); toast.success('Plano excluído'); setDeleteId(null); load(); } catch { toast.error('Erro'); } };

  return (
    <div>
      <PageHeader title="Manutenção Preventiva" subtitle="Planos automáticos com cálculo de próximo vencimento" onAction={openCreate} actionLabel="Novo Plano" />
      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4 mb-4 flex gap-3">
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="overdue">Vencidas</SelectItem>
            <SelectItem value="due_soon">Próximas</SelectItem>
            <SelectItem value="ok">Em dia</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-neutral-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Veículo</th>
              <th className="text-left px-4 py-3">Plano</th>
              <th className="text-left px-4 py-3">Intervalo</th>
              <th className="text-left px-4 py-3">Próximo</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody data-testid="plans-table" className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {loading ? <tr><td colSpan={6} className="p-4"><TableSkeleton rows={5} cols={6} /></td></tr> :
              items.length === 0 ? <tr><td colSpan={6}><EmptyState icon={ClipboardCheck} title="Nenhum plano" /></td></tr> :
              items.map(p => (
                <tr key={p.id} className="tr-hover">
                  <td className="px-4 py-3 font-mono text-xs">{p.vehicle_plate}</td>
                  <td className="px-4 py-3"><div className="font-medium">{p.name}</div><div className="text-xs text-neutral-500">{p.maintenance_type_name || '-'}</div></td>
                  <td className="px-4 py-3 text-xs">{p.interval_km ? `${p.interval_km} km` : ''}{p.interval_km && p.interval_months ? ' • ' : ''}{p.interval_months ? `${p.interval_months} meses` : ''}</td>
                  <td className="px-4 py-3 text-xs">
                    {p.next?.next_km && <div>KM: {p.next.next_km.toLocaleString('pt-BR')} <span className="text-neutral-400">({p.next.km_left?.toLocaleString('pt-BR')} restantes)</span></div>}
                    {p.next?.next_date && <div>Data: {new Date(p.next.next_date).toLocaleDateString('pt-BR')} <span className="text-neutral-400">({p.next.days_left} dias)</span></div>}
                  </td>
                  <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${statusTone(p.next?.plan_status)}`}>{statusLabel(p.next?.plan_status)}</span></td>
                  <td className="px-4 py-3 text-right">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(p)}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="text-red-600" onClick={() => setDeleteId(p.id)}><Trash2 className="h-4 w-4" /></Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
        <Pagination page={page} total={total} limit={10} onPageChange={setPage} />
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? 'Editar' : 'Novo'} plano preventivo</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5 md:col-span-2">
              <Label>Veículo *</Label>
              <Select value={form.vehicle_id} onValueChange={v => setForm(f => ({ ...f, vehicle_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.plate} - {v.brand} {v.model}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Nome do plano *</Label>
              <Input required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Troca de óleo" />
            </div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={form.maintenance_type_id || 'none'} onValueChange={v => {
                if (v === 'none') { setForm(f => ({ ...f, maintenance_type_id: '', maintenance_type_name: '' })); return; }
                const t = types.find(x => x.id === v); setForm(f => ({ ...f, maintenance_type_id: v, maintenance_type_name: t?.name || '' }));
              }}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {types.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Intervalo (km)</Label><Input type="number" value={form.interval_km} onChange={e => setForm(f => ({ ...f, interval_km: e.target.value }))} placeholder="10000" /></div>
            <div className="space-y-1.5"><Label>Intervalo (meses)</Label><Input type="number" value={form.interval_months} onChange={e => setForm(f => ({ ...f, interval_months: e.target.value }))} placeholder="6" /></div>
            <div className="space-y-1.5"><Label>Última execução (km)</Label><Input type="number" value={form.last_done_km} onChange={e => setForm(f => ({ ...f, last_done_km: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Última execução (data)</Label><Input type="date" value={form.last_done_date} onChange={e => setForm(f => ({ ...f, last_done_date: e.target.value }))} /></div>
            <div className="space-y-1.5 md:col-span-2"><Label>Responsável</Label><Input value={form.responsible_name} onChange={e => setForm(f => ({ ...f, responsible_name: e.target.value }))} /></div>
            <div className="space-y-1.5 md:col-span-2"><Label>Observações</Label><Textarea rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-brand hover:bg-[#A81E23] text-white">{saving ? 'Salvando...' : 'Salvar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={o => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Excluir plano?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
